import type { PerformancePoint } from "../api";

// Lightweight SVG line chart for valuation series — no chart library. The
// account series draws in fjord; an optional benchmark (rebased server-side
// to the same starting value) draws as a dashed amber line.

const W = 720;
const H = 200;
const PAD = { top: 12, right: 12, bottom: 24, left: 82 };

export function ValueChart({
  series,
  benchmark,
  benchmarkName,
  currency,
}: {
  series: PerformancePoint[];
  benchmark?: PerformancePoint[] | null;
  benchmarkName?: string | null;
  currency: string;
}) {
  if (series.length < 2) {
    return <div className="muted">Not enough history to chart yet.</div>;
  }

  const all = [...series, ...(benchmark ?? [])];
  let min = Math.min(...all.map((p) => p.value));
  let max = Math.max(...all.map((p) => p.value));
  if (max === min) {
    max += 1;
    min -= 1;
  }
  const pad = (max - min) * 0.06;
  min -= pad;
  max += pad;

  const t0 = Date.parse(series[0]!.date);
  const t1 = Date.parse(series[series.length - 1]!.date);
  const x = (date: string) =>
    PAD.left + ((Date.parse(date) - t0) / (t1 - t0 || 1)) * (W - PAD.left - PAD.right);
  const y = (value: number) =>
    PAD.top + (1 - (value - min) / (max - min)) * (H - PAD.top - PAD.bottom);

  const path = (points: PerformancePoint[]) =>
    points
      .filter((p) => Date.parse(p.date) >= t0 && Date.parse(p.date) <= t1)
      .map((p, i) => `${i === 0 ? "M" : "L"}${x(p.date).toFixed(1)},${y(p.value).toFixed(1)}`)
      .join(" ");

  const fmt = (v: number) =>
    `${currency} ${Math.round(v).toLocaleString("en-GB")}`;
  const gridYs = [min + (max - min) * 0.1, (min + max) / 2, min + (max - min) * 0.9];

  return (
    <figure className="value-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Account value over time">
        {gridYs.map((v) => (
          <g key={v}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(v)}
              y2={y(v)}
              className="chart-grid"
            />
            <text x={PAD.left - 6} y={y(v) + 3} className="chart-label" textAnchor="end">
              {fmt(v)}
            </text>
          </g>
        ))}
        {benchmark && benchmark.length >= 2 && (
          <path d={path(benchmark)} className="chart-line-benchmark" />
        )}
        <path d={path(series)} className="chart-line-main" />
        <text x={PAD.left} y={H - 6} className="chart-label">
          {series[0]!.date}
        </text>
        <text x={W - PAD.right} y={H - 6} className="chart-label" textAnchor="end">
          {series[series.length - 1]!.date}
        </text>
      </svg>
      {benchmark && benchmarkName && (
        <figcaption className="chart-legend">
          <span className="legend-swatch swatch-main" /> Account value
          <span className="legend-swatch swatch-benchmark" /> {benchmarkName} (rebased)
        </figcaption>
      )}
    </figure>
  );
}
